import React from "react";

export function Overline({ children, className = "" }) {
  return (
    <span className={`text-[10px] uppercase tracking-overline text-slate-400 font-medium ${className}`}>
      {children}
    </span>
  );
}

const ACCENTS = {
  cyan: "text-aegis-cyan",
  red: "text-aegis-red",
  amber: "text-aegis-amber",
  purple: "text-aegis-purple",
  emerald: "text-aegis-emerald",
  slate: "text-slate-300",
};

export function Panel({ title, subtitle, icon: Icon, accent = "cyan", actions, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`relative rounded-xl border border-edge bg-panel ${className}`}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-edge">
          <div className="flex items-center gap-3">
            {Icon && (
              <span className={`grid place-items-center h-9 w-9 rounded-lg border border-edge raise ${ACCENTS[accent]}`}>
                <Icon size={18} strokeWidth={1.6} />
              </span>
            )}
            <div>
              {title && <h2 className="font-heading text-[15px] font-medium text-slate-100 tracking-tight">{title}</h2>}
              {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {actions}
        </div>
      )}
      <div className="p-6">{children}</div>
    </section>
  );
}

export function Btn({ children, onClick, variant = "ghost", className = "", testid, disabled, type = "button" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-slate-500 px-4 py-2";
  const variants = {
    ghost: "border border-edge text-slate-300 hover:text-slate-100 hover:border-edgeStrong bg-slate-900/40",
    cyan: "bg-aegis-cyan/10 text-aegis-cyan border border-aegis-cyan/30 hover:bg-aegis-cyan/20",
    solidCyan: "bg-slate-800 text-slate-100 font-semibold border border-slate-700 hover:bg-slate-700",
    red: "bg-aegis-red text-slate-100 hover:bg-aegis-red/90",
    redGhost: "border border-aegis-red/40 text-aegis-red bg-aegis-red/10 hover:bg-aegis-red/20",
    amber: "bg-aegis-amber/15 text-aegis-amber border border-aegis-amber/40 hover:bg-aegis-amber/25",
    purple: "bg-aegis-purple/15 text-aegis-purple border border-aegis-purple/40 hover:bg-aegis-purple/25",
    emerald: "bg-aegis-emerald/10 text-aegis-emerald border border-aegis-emerald/40 hover:bg-aegis-emerald/20",
  };
  return (
    <button type={type} data-testid={testid} disabled={disabled} onClick={onClick} className={`${base} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function RiskBadge({ level }) {
  const high = level === "HIGH_RISK";
  const cls = high
    ? "text-aegis-red border-aegis-red/40 bg-aegis-red/10"
    : "text-aegis-amber border-aegis-amber/40 bg-aegis-amber/10";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-mono font-medium tracking-wider ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${high ? "bg-aegis-red" : "bg-aegis-amber"} animate-pulseGlow`} />
      {level}
    </span>
  );
}

export function Dot({ color = "emerald", ping = true }) {
  const map = { emerald: "bg-aegis-emerald", cyan: "bg-aegis-cyan", red: "bg-aegis-red", amber: "bg-aegis-amber", slate: "bg-slate-500" };
  return (
    <span className="relative flex h-2 w-2">
      {ping && <span className={`absolute inline-flex h-full w-full rounded-full ${map[color]} opacity-60 animate-ping`} />}
      <span className={`relative inline-flex h-2 w-2 rounded-full ${map[color]}`} />
    </span>
  );
}
